import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { OurSquareLogo } from '../../components/brand/Logo';
import { 
  AlertTriangle, Shield, TrendingUp, TrendingDown, User, 
  ChevronRight, ArrowLeft, Activity, RefreshCw, Edit,
  CheckCircle, XCircle, Clock, BarChart3
} from 'lucide-react';

const SCORE_COLORS = {
  high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
  moderate: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  low: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
};

const getScoreLevel = (score) => {
  if (score <= 3) return 'high';
  if (score <= 6) return 'moderate';
  return 'low';
};

const getScoreLabel = (score) => {
  if (score <= 3) return 'High Risk';
  if (score <= 6) return 'Moderate Risk';
  return 'Low Risk';
};

export default function RiskScoringPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [dashRes, alertsRes] = await Promise.all([
        api.get('/risk-scoring/dashboard?entity=our_square'),
        api.get('/risk-scoring/alerts?entity=our_square'),
      ]);
      setDashboard(dashRes.data);
      setAlerts(alertsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const recalculateAll = async () => {
    try {
      await api.post('/risk-scoring/recalculate-all?entity=our_square');
      toast.success('All scores recalculated!');
      load();
    } catch (e) { toast.error('Failed to recalculate'); }
  };

  if (selectedResident) {
    return <ResidentRiskDetail residentId={selectedResident.resident_id} residentName={selectedResident.resident_name} api={api} onBack={() => { setSelectedResident(null); load(); }} />;
  }

  if (loading || !dashboard) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="risk-scoring-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <OurSquareLogo size="sm" className="h-12 w-auto" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Risk & Stability Scoring</h1>
            <p className="text-sm text-muted-foreground">Monitor resident stability and identify those needing support</p>
          </div>
        </div>
        <Button onClick={recalculateAll} variant="outline" className="rounded-full gap-2">
          <RefreshCw size={14} /> Recalculate All
        </Button>
      </div>

      {/* Score Legend */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500"></div>
              <span className="text-sm"><strong>1-3:</strong> High Risk (sloping, relapse, risky behaviors)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-amber-500"></div>
              <span className="text-sm"><strong>4-6:</strong> Moderate Risk (needs monitoring)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-emerald-500"></div>
              <span className="text-sm"><strong>7-10:</strong> Low Risk (high success potential)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center mb-2"><User size={18} /></div>
            <p className="text-2xl font-bold text-slate-900">{dashboard.total_residents}</p>
            <p className="text-xs text-muted-foreground">Total Residents</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-red-50 text-red-700 flex items-center justify-center mb-2"><AlertTriangle size={18} /></div>
            <p className="text-2xl font-bold text-red-600">{dashboard.high_risk}</p>
            <p className="text-xs text-muted-foreground">High Risk (1-3)</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center mb-2"><Activity size={18} /></div>
            <p className="text-2xl font-bold text-amber-600">{dashboard.moderate_risk}</p>
            <p className="text-xs text-muted-foreground">Moderate Risk (4-6)</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2"><Shield size={18} /></div>
            <p className="text-2xl font-bold text-emerald-600">{dashboard.low_risk}</p>
            <p className="text-xs text-muted-foreground">Low Risk (7-10)</p>
          </CardContent>
        </Card>
      </div>

      {/* Critical Alerts */}
      {alerts.alerts?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <AlertTriangle size={18} /> Active Risk Alerts ({alerts.total_alerts})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alerts.alerts.slice(0, 5).map(alert => (
                <div key={alert.id} className={`flex items-center justify-between p-3 rounded-lg ${alert.severity === 'critical' ? 'bg-red-50' : 'bg-amber-50'} cursor-pointer hover:opacity-80`} onClick={() => setSelectedResident({ resident_id: alert.resident_id, resident_name: alert.resident_name })}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${alert.severity === 'critical' ? 'bg-red-500' : 'bg-amber-500'}`}>
                      {alert.score}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{alert.resident_name}</p>
                      <p className={`text-xs ${alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>{alert.message}</p>
                    </div>
                  </div>
                  <Badge className={alert.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                    {alert.severity === 'critical' ? 'Critical' : 'Warning'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Residents Sorted by Risk */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Residents by Risk Score</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {dashboard.residents.map(r => {
              const level = getScoreLevel(r.score);
              const colors = SCORE_COLORS[level];
              
              return (
                <div key={r.resident_id} className={`flex items-center justify-between p-4 rounded-xl ${colors.bg} ${colors.border} border cursor-pointer hover:shadow-md transition-shadow`} onClick={() => setSelectedResident(r)} data-testid={`resident-${r.resident_id}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl text-white ${r.score <= 3 ? 'bg-red-500' : r.score <= 6 ? 'bg-amber-500' : 'bg-emerald-500'}`}>
                      {r.score}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{r.resident_name}</p>
                      <p className="text-xs text-muted-foreground">{r.program_phase} phase</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={colors.badge}>{r.risk_level}</Badge>
                    <ChevronRight size={16} className="text-muted-foreground" />
                  </div>
                </div>
              );
            })}
            {dashboard.residents.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No active residents</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ResidentRiskDetail({ residentId, residentName, api, onBack }) {
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ score: '', reason: '', notes: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [residentId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/risk-scoring/resident/${residentId}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const submitManualScore = async () => {
    if (!manualForm.score || !manualForm.reason) {
      return toast.error('Score and reason are required');
    }
    const scoreNum = parseInt(manualForm.score);
    if (scoreNum < 1 || scoreNum > 10) {
      return toast.error('Score must be between 1 and 10');
    }
    try {
      await api.post(`/risk-scoring/resident/${residentId}/manual`, {
        score: scoreNum,
        reason: manualForm.reason,
        notes: manualForm.notes,
      });
      toast.success('Score updated!');
      setEditOpen(false);
      setManualForm({ score: '', reason: '', notes: '' });
      load();
    } catch (e) { toast.error('Failed to update'); }
  };

  if (loading || !data) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  const score = data.current_score;
  const level = getScoreLevel(score.score);
  const colors = SCORE_COLORS[level];
  const breakdown = score.breakdown || {};

  return (
    <div className="space-y-6" data-testid="resident-risk-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="rounded-full"><ArrowLeft size={16} /></Button>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{residentName}</h2>
            <p className="text-xs text-muted-foreground">Risk & Stability Assessment</p>
          </div>
        </div>
        <Button onClick={() => setEditOpen(true)} className="rounded-full gap-2 bg-cyan-600 hover:bg-cyan-700 text-white">
          <Edit size={14} /> Adjust Score
        </Button>
      </div>

      {/* Current Score */}
      <Card className={`border-0 shadow-sm rounded-xl ${colors.bg} border-2 ${colors.border}`}>
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Current Stability Score</p>
              <div className="flex items-baseline gap-2">
                <span className={`text-6xl font-bold ${colors.text}`}>{score.score}</span>
                <span className="text-2xl text-muted-foreground">/ 10</span>
              </div>
              <Badge className={`${colors.badge} mt-3`}>{score.risk_level}</Badge>
            </div>
            <div className={`w-24 h-24 rounded-full flex items-center justify-center ${score.score <= 3 ? 'bg-red-500' : score.score <= 6 ? 'bg-amber-500' : 'bg-emerald-500'}`}>
              {score.score <= 3 ? <TrendingDown size={48} className="text-white" /> : score.score <= 6 ? <Activity size={48} className="text-white" /> : <TrendingUp size={48} className="text-white" />}
            </div>
          </div>
          <p className={`mt-4 text-sm ${colors.text}`}>{score.risk_description}</p>
        </CardContent>
      </Card>

      {/* Score Breakdown */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><BarChart3 size={16} /> Score Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Base Score */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <span className="text-sm font-medium">Base Score</span>
              <span className="font-bold">{breakdown.base || 7}</span>
            </div>

            {/* Milestones */}
            {breakdown.milestones && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-emerald-600" />
                  <span className="text-sm">Milestones ({breakdown.milestones.count}/8)</span>
                </div>
                <span className={`font-bold ${breakdown.milestones.adjustment >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {breakdown.milestones.adjustment >= 0 ? '+' : ''}{breakdown.milestones.adjustment}
                </span>
              </div>
            )}

            {/* Incidents */}
            {breakdown.incidents && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} className="text-red-600" />
                  <span className="text-sm">Incidents (last 90 days: {breakdown.incidents.count})</span>
                </div>
                <span className={`font-bold ${breakdown.incidents.adjustment >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {breakdown.incidents.adjustment >= 0 ? '+' : ''}{breakdown.incidents.adjustment}
                </span>
              </div>
            )}

            {/* Curfew */}
            {breakdown.curfew_violations && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-amber-600" />
                  <span className="text-sm">Curfew Violations ({breakdown.curfew_violations.count})</span>
                </div>
                <span className={`font-bold ${breakdown.curfew_violations.adjustment >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {breakdown.curfew_violations.adjustment >= 0 ? '+' : ''}{breakdown.curfew_violations.adjustment}
                </span>
              </div>
            )}

            {/* Meeting Attendance */}
            {breakdown.meeting_attendance && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-cyan-600" />
                  <span className="text-sm">Meeting Attendance ({breakdown.meeting_attendance.rate}%)</span>
                </div>
                <span className={`font-bold ${breakdown.meeting_attendance.adjustment >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {breakdown.meeting_attendance.adjustment >= 0 ? '+' : ''}{breakdown.meeting_attendance.adjustment}
                </span>
              </div>
            )}

            {/* Drug Tests */}
            {breakdown.drug_tests && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-purple-600" />
                  <span className="text-sm">Drug Tests ({breakdown.drug_tests.rate}% passed)</span>
                </div>
                <span className={`font-bold ${breakdown.drug_tests.adjustment >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {breakdown.drug_tests.adjustment >= 0 ? '+' : ''}{breakdown.drug_tests.adjustment}
                </span>
              </div>
            )}

            {/* Tenure */}
            {breakdown.tenure && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-emerald-600" />
                  <span className="text-sm">Program Tenure ({breakdown.tenure.days} days)</span>
                </div>
                <span className={`font-bold ${breakdown.tenure.adjustment >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  +{breakdown.tenure.adjustment}
                </span>
              </div>
            )}

            {/* Manual Override */}
            {breakdown.manual_override && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-cyan-50 border border-cyan-200">
                <div className="flex items-center gap-2">
                  <Edit size={16} className="text-cyan-600" />
                  <div>
                    <span className="text-sm font-medium">Staff Override ({breakdown.manual_override.weight})</span>
                    <p className="text-xs text-cyan-600">{breakdown.manual_override.reason}</p>
                  </div>
                </div>
                <span className="font-bold text-cyan-700">{breakdown.manual_override.value}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Score History */}
      {data.history?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Score History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.history.slice(0, 10).map(h => (
                <div key={h.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${h.score <= 3 ? 'bg-red-500' : h.score <= 6 ? 'bg-amber-500' : 'bg-emerald-500'}`}>
                      {h.score}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{h.type === 'manual' ? 'Staff Adjustment' : 'Auto-calculated'}</p>
                      {h.reason && <p className="text-xs text-muted-foreground">{h.reason}</p>}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Score Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Adjust Risk Score</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="p-3 bg-cyan-50 rounded-lg text-sm text-cyan-700">
              Manual adjustments are weighted at 60% and blended with the auto-calculated score (40%).
            </div>
            <div>
              <Label>New Score (1-10) *</Label>
              <Select value={manualForm.score} onValueChange={v => setManualForm({ ...manualForm, score: v })}>
                <SelectTrigger><SelectValue placeholder="Select score" /></SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <SelectItem key={n} value={String(n)}>
                      {n} - {n <= 3 ? 'High Risk' : n <= 6 ? 'Moderate Risk' : 'Low Risk'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason for Adjustment *</Label>
              <Select value={manualForm.reason} onValueChange={v => setManualForm({ ...manualForm, reason: v })}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Positive behavioral change observed">Positive behavioral change observed</SelectItem>
                  <SelectItem value="Recent relapse or concerning behavior">Recent relapse or concerning behavior</SelectItem>
                  <SelectItem value="Increased engagement with program">Increased engagement with program</SelectItem>
                  <SelectItem value="Disengagement from program">Disengagement from program</SelectItem>
                  <SelectItem value="Family/personal crisis">Family/personal crisis</SelectItem>
                  <SelectItem value="Employment progress">Employment progress</SelectItem>
                  <SelectItem value="Housing stability concern">Housing stability concern</SelectItem>
                  <SelectItem value="Staff recommendation">Staff recommendation</SelectItem>
                  <SelectItem value="Other">Other (see notes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Additional Notes</Label>
              <DictationInput value={manualForm.notes} onChange={e => setManualForm({ ...manualForm, notes: e.target.value })} rows={3} placeholder="Provide context for this adjustment..." showCleanup={false} />
            </div>
            <Button onClick={submitManualScore} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">
              Update Score
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
